import analytics, { AnalyticsEvent } from "./analytics";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

/** `show` merges podcast channels, programs and livestream channels/programs with the same name. */
export type ChatEntityType = "show" | "program" | "channel" | "brand";
export type ChatEntityScope = "explorer" | "miContenido";

export interface ChatEntity {
  type: ChatEntityType;
  scope: ChatEntityScope;
  id: string;
  name: string;
  subtitle?: string;
  image?: ChatEntityImage;
  /** Every source id behind a `show`; sent back so the chat can query all of them. */
  refs?: {
    podcastIds?: string[];
    programIds?: string[];
    livestreamChannelIds?: string[];
    livestreamProgramIds?: string[];
  };
}

export interface ChatHistoryMessage {
  role: "user" | "assistant";
  text: string;
  mentions?: ChatEntity[];
  /** Chat action picked with `/` (its id). */
  action?: string;
}

function authHeaders(): Record<string, string> {
  try {
    const token = localStorage.getItem("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** Programs, channels and brands the user can reference with @ (scoped by their products). */
export async function searchChatEntities(
  query: string,
  signal?: AbortSignal,
  ownContentOnly = false,
): Promise<ChatEntity[]> {
  const response = await fetch(
    `${API_BASE_URL}/api/agent/entities?q=${encodeURIComponent(query)}${ownContentOnly ? "&scope=own" : ""}`,
    { headers: authHeaders(), signal },
  );
  if (!response.ok) return [];
  const body = await response.json();
  return Array.isArray(body?.entities) ? (body.entities as ChatEntity[]) : [];
}

/** Top programs and brands the @ menu offers before anything is typed. */
export interface SuggestedChatEntities {
  programs: ChatEntity[];
  brands: ChatEntity[];
}

export async function fetchSuggestedChatEntities(ownContentOnly = false): Promise<SuggestedChatEntities> {
  const response = await fetch(
    `${API_BASE_URL}/api/agent/entities/suggested${ownContentOnly ? "?scope=own" : ""}`,
    { headers: authHeaders() },
  );
  if (!response.ok) throw new Error(`Suggested entities failed (${response.status})`);
  const body = await response.json();
  return {
    programs: Array.isArray(body?.programs) ? body.programs : [],
    brands: Array.isArray(body?.brands) ? body.brands : [],
  };
}

/** Chat access from "Acceso (nuevo)"; the chat UI reads this instead of the app products. */
export interface ChatAccessInfo {
  allowed: boolean;
  miContenido: boolean;
  misPodcasts: boolean;
  explorerSections: { programs: boolean; sponsors: boolean; livestreams: boolean };
  /** Enabled chat actions, as the `/` menu lists them. */
  actions: ChatSlashAction[];
}

export interface ChatSlashAction {
  id: string;
  label: string;
  description: string;
  /** While picked, @ mentions and the answer use only the user's own content. */
  ownContentOnly?: boolean;
}

export async function fetchChatAccess(): Promise<ChatAccessInfo> {
  const response = await fetch(`${API_BASE_URL}/api/chat/access`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`Chat access failed (${response.status})`);
  return (await response.json()) as ChatAccessInfo;
}

/** Prefilled `/mi-contenido/crear-coleccion` or `/mis-podcasts/crear-coleccion` link; the user reviews and saves the form there. */
export interface ChatCollectionLink {
  url: string;
  startDate?: string;
  endDate?: string;
  channels?: string[];
  /** Single channel, in links saved before `channels`. */
  channel?: string;
  programs: string[];
  brands: string[];
  platforms?: string[];
  /** Logo or cover per listed name, by kind. */
  images?: Partial<Record<"channels" | "programs" | "brands", Record<string, ChatEntityImage>>>;
  /** Suggested collection name; also in the url as `title`. */
  title?: string;
  /** No channel or program was asked for, so `channels` lists every own channel. */
  allChannels?: boolean;
  /** Platform keys behind `platforms` (the form's `sources`). */
  platformKeys?: string[];
  /** Ids behind the listed names; missing in cards saved before the card was editable. */
  selection?: {
    channels: Array<{ id: string; name: string }>;
    programs: Array<{ id: string; name: string; podcastId: string }>;
    brands: Array<{ id: string; name: string }>;
  };
}

/** Raw image fields for an entity link in an answer (brand logo or program/channel cover). */
export interface ChatEntityImage {
  s3Key?: string;
  url?: string;
  /** The channel's cover, shown when a program's own cover fails to load. */
  fallback?: { s3Key?: string; url?: string };
}

/** Prefilled export dialog (PDF/Excel) shown by a verify action. */
export interface ChatVerifyExport {
  reportType: string;
  title: string;
  params: { startDate?: string; endDate?: string; country?: "cl" | "ar" };
  /** Dialog filter selections (ids), keyed by the report's filter keys. */
  filters: Record<string, string[]>;
  labels: Partial<Record<"brands" | "channels" | "programs" | "categories", string[]>>;
  /** Logo or cover per label, same order; null when the entity has none. */
  images?: Partial<Record<"brands" | "channels" | "programs", Array<ChatEntityImage | null>>>;
  /** Temporalidad the dialog opens with; omitted = whole period. */
  granularity?: "month" | "week";
  /** Reports the dialog lets the user switch to. */
  alternatives?: Array<{ reportType: string; label: string; description?: string }>;
}

/** A program's public media kit link, or where to turn the podcast's media kit on. */
export interface ChatMediaKitLink {
  program: string;
  channel: string;
  /** Public media kit path; absent while the podcast's media kit is off. */
  url?: string;
  enabled: boolean;
  settingsUrl: string;
  image?: ChatEntityImage;
}

export interface ChatActionPayloads {
  create_collection: ChatCollectionLink;
  create_media_kit: ChatMediaKitLink;
  verify_programs: ChatVerifyExport;
  verify_sponsors: ChatVerifyExport;
  verify_mentions: ChatVerifyExport;
  verify_livestreams: ChatVerifyExport;
}

export type ChatActionKind = keyof ChatActionPayloads;

/** A card a chat action showed with a reply. Unknown kinds (newer server) are skipped. */
export type ChatAction = {
  [K in ChatActionKind]: { kind: K; payload: ChatActionPayloads[K] };
}[ChatActionKind];

export type ChatStreamEvent =
  | { type: "turn"; turnId: string }
  | { type: "status"; text: string }
  | {
      type: "text";
      text: string;
      entityImages?: Record<string, ChatEntityImage>;
    }
  | ({ type: "action" } & ChatAction)
  | { type: "error"; message: string }
  | { type: "done" };

export interface ChatConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface StoredChatMessage {
  role: "user" | "assistant";
  text: string;
  mentions?: ChatEntity[];
  /** Chat action the user picked with `/` for this message. */
  action?: string;
  actions?: ChatAction[];
  /** Collection cards stored before actions existed; read as `create_collection` actions. */
  collectionLinks?: ChatCollectionLink[];
  entityImages?: Record<string, ChatEntityImage>;
  error?: string;
  turnId?: string;
}

/** Action cards of a stored reply, including collection cards saved before actions existed. */
export function storedMessageActions(message: StoredChatMessage): ChatAction[] {
  if (message.actions?.length) return message.actions;
  return (message.collectionLinks ?? []).map((payload) => ({
    kind: "create_collection",
    payload,
  }));
}

export interface ChatConversation extends ChatConversationSummary {
  messages: StoredChatMessage[];
}

export type ChatClientEventType =
  | "entity_link_click"
  | "link_click"
  | "collection_link_click"
  | "verify_export_click"
  | "media_kit_click"
  | "collection_saved"
  | "stop_generation";

const AMPLITUDE_CHAT_EVENTS: Record<ChatClientEventType, AnalyticsEvent> = {
  entity_link_click: AnalyticsEvent.CHAT_ENTITY_LINK_CLICK,
  link_click: AnalyticsEvent.CHAT_LINK_CLICK,
  collection_link_click: AnalyticsEvent.CHAT_COLLECTION_LINK_CLICK,
  verify_export_click: AnalyticsEvent.CHAT_VERIFY_EXPORT_CLICK,
  media_kit_click: AnalyticsEvent.CHAT_MEDIA_KIT_CLICK,
  collection_saved: AnalyticsEvent.CHAT_COLLECTION_SAVED,
  stop_generation: AnalyticsEvent.CHAT_STOP_GENERATION,
};

/**
 * Reports a chat action. `keepalive` lets the request finish while the click navigates away;
 * `sendBeacon` is not used because it cannot send the `Authorization` header.
 */
export function trackChatEvent(
  type: ChatClientEventType,
  context: { conversationId?: string | null; turnId?: string | null },
  payload?: Record<string, unknown>,
): void {
  const body = {
    type,
    ...(context.conversationId ? { conversationId: context.conversationId } : {}),
    ...(context.turnId ? { turnId: context.turnId } : {}),
    ...(payload ? { payload } : {}),
  };
  analytics.track(AMPLITUDE_CHAT_EVENTS[type], { ...payload, ...body });
  fetch(`${API_BASE_URL}/api/chat/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => undefined);
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return typeof body?.message === "string" ? body.message : fallback;
  } catch {
    return fallback;
  }
}

export async function listChatConversations(signal?: AbortSignal): Promise<ChatConversationSummary[]> {
  const response = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) return [];
  const body = await response.json();
  return Array.isArray(body?.conversations) ? body.conversations : [];
}

/** Null when the conversation does not exist or belongs to someone else. */
export async function getChatConversation(id: string, signal?: AbortSignal): Promise<ChatConversation | null> {
  const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
    signal,
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await errorMessage(response, "No pudimos cargar la conversación."));
  return (await response.json()) as ChatConversation;
}

/** Hides the conversation from the user; it stays stored for the usage admin. */
export async function deleteChatConversation(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error(await errorMessage(response, "No pudimos eliminar la conversación."));
}

/** Creates a conversation whose first message is `message`; the reply is streamed separately. */
export async function createChatConversation(message: ChatHistoryMessage): Promise<{ id: string; title: string }> {
  const response = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ text: message.text, mentions: message.mentions, action: message.action }),
  });
  if (!response.ok) throw new Error(await errorMessage(response, "No pudimos iniciar la conversación."));
  return response.json();
}

/**
 * Streams `POST /api/chat` as newline-delimited JSON events. The server reads the thread from
 * `conversationId`, appends `message` when given, and stores the reply.
 */
export async function streamChat(
  request: { conversationId: string; message?: ChatHistoryMessage },
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok || !response.body) {
    let message = "No pudimos responder en este momento.";
    try {
      const body = await response.json();
      if (typeof body?.message === "string") message = body.message;
    } catch {
      /* keep default */
    }
    onEvent({ type: "error", message });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newlineIndex = buffer.indexOf("\n");
    while (newlineIndex >= 0) {
      const line = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);
      if (line) onEvent(JSON.parse(line) as ChatStreamEvent);
      newlineIndex = buffer.indexOf("\n");
    }
  }
  const rest = buffer.trim();
  if (rest) onEvent(JSON.parse(rest) as ChatStreamEvent);
}
